import {
  fetchPortfolioRecordDocumentQuota,
  fetchPortfolioRecords,
  savePortfolioRecords,
} from "@/utils";
import { buildAuthPayload } from "@/utils/MyEstateHelper";

export type PortfolioRecordCategory =
  | "INSURANCE"
  | "LEASE"
  | "TAX"
  | "INSPECTION"
  | "WARRANTY"
  | "OTHER";

export type PortfolioRecordRepeatUnit = "MONTH" | "YEAR";
export type PortfolioRecordReminderUnit = "DAY" | "MONTH";

export type PortfolioRecordDocument = {
  id: string;
  name: string;
  size: number;
  type: string;
  blob: Blob;
  contentBase64?: string;
};

export type PortfolioRecord = {
  id: string;
  estateId: number;
  title: string;
  category: PortfolioRecordCategory;
  note: string;
  importantDate: string | null;
  repeatInterval: number | null;
  repeatUnit: PortfolioRecordRepeatUnit | null;
  reminderAmount: number | null;
  reminderUnit: PortfolioRecordReminderUnit | null;
  documents: PortfolioRecordDocument[];
  createdAt: string;
};

export type PortfolioRecordDocumentQuota = {
  count: number;
  maximum: number;
  limitReached: boolean;
};

function parsePortfolioRecordDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12);
}

function addPortfolioRecordInterval(
  base: Date,
  count: number,
  unit: PortfolioRecordRepeatUnit,
) {
  const targetYear =
    base.getFullYear() +
    (unit === "YEAR" ? count : Math.floor((base.getMonth() + count) / 12));
  const targetMonth =
    unit === "YEAR" ? base.getMonth() : (base.getMonth() + count) % 12;
  const lastDay = new Date(targetYear, targetMonth + 1, 0, 12).getDate();
  return new Date(
    targetYear,
    targetMonth,
    Math.min(base.getDate(), lastDay),
    12,
  );
}

export function getNextPortfolioRecordOccurrence(
  record: PortfolioRecord,
  today: Date,
) {
  if (!record.importantDate) return null;

  const base = parsePortfolioRecordDate(record.importantDate);
  if (!record.repeatInterval || !record.repeatUnit || base >= today)
    return base;

  const elapsed =
    record.repeatUnit === "YEAR"
      ? today.getFullYear() - base.getFullYear()
      : (today.getFullYear() - base.getFullYear()) * 12 +
        today.getMonth() -
        base.getMonth();
  let occurrence = Math.max(
    record.repeatInterval,
    Math.floor(elapsed / record.repeatInterval) * record.repeatInterval,
  );
  let next = addPortfolioRecordInterval(
    base,
    occurrence,
    record.repeatUnit,
  );

  while (next < today) {
    occurrence += record.repeatInterval;
    next = addPortfolioRecordInterval(base, occurrence, record.repeatUnit);
  }

  return next;
}

type PortfolioRecordAction = "ADD" | "EDIT" | "DELETE";

type AuthUser = {
  email: string;
  token: string;
};

type ApiPortfolioRecordDocument = Omit<PortfolioRecordDocument, "blob"> & {
  contentBase64?: string;
};

type ApiPortfolioRecord = Omit<PortfolioRecord, "documents"> & {
  documents: ApiPortfolioRecordDocument[];
};

function blobToBase64(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(
        typeof result === "string" && result.includes(",")
          ? result.split(",")[1]
          : "",
      );
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(contentBase64: string, type: string) {
  if (!contentBase64) return new Blob([], { type });
  const binary = window.atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type });
}

async function toApiRecord(
  record: PortfolioRecord,
  type: PortfolioRecordAction,
): Promise<ApiPortfolioRecord & { type: PortfolioRecordAction }> {
  return {
    ...record,
    type,
    documents: await Promise.all(
      record.documents.map(async (document) => ({
        id: document.id,
        name: document.name,
        size: document.size,
        type: document.type,
        contentBase64:
          document.contentBase64 ?? (await blobToBase64(document.blob)),
      })),
    ),
  };
}

function fromApiRecord(record: ApiPortfolioRecord): PortfolioRecord {
  return {
    ...record,
    documents: (record.documents ?? []).map((document) => ({
      ...document,
      blob: base64ToBlob(document.contentBase64 ?? "", document.type),
    })),
  };
}

export async function getPortfolioRecords(estateId: number, user: AuthUser) {
  const payload = buildAuthPayload(user.email, user.token, { estateId });
  const response = await fetchPortfolioRecords(payload, user.token);
  if (response.status !== 200 || !response.data?.success) {
    throw new Error(
      response.data?.message ?? "Failed to load portfolio records",
    );
  }
  return ((response.data?.data ?? []) as ApiPortfolioRecord[]).map(
    fromApiRecord,
  );
}

export async function getPortfolioRecordDocumentQuota(user: AuthUser) {
  const payload = buildAuthPayload(user.email, user.token);
  const response = await fetchPortfolioRecordDocumentQuota(payload, user.token);
  if (response.status !== 200 || !response.data?.success) {
    throw new Error(response.data?.message ?? "Failed to load document quota");
  }
  const quota = response.data?.data as
    | Partial<PortfolioRecordDocumentQuota>
    | undefined;
  const count = Number(quota?.count ?? 0);
  const maximum = Number(quota?.maximum ?? 5);
  return {
    count,
    maximum,
    limitReached: quota?.limitReached === true || count >= maximum,
  };
}

export async function savePortfolioRecord(
  record: PortfolioRecord,
  user: AuthUser,
  action: Extract<PortfolioRecordAction, "ADD" | "EDIT"> = "ADD",
) {
  const payload = buildAuthPayload(user.email, user.token, {
    estateId: record.estateId,
    record: await toApiRecord(record, action),
  });
  const response = await savePortfolioRecords(payload, user.token);
  if (response.status !== 200 || !response.data?.success) {
    throw new Error(
      response.data?.message ?? "Failed to save portfolio record",
    );
  }
  const savedRecord = (response.data?.data ?? [])[0] as
    | ApiPortfolioRecord
    | undefined;
  return savedRecord ? fromApiRecord(savedRecord) : record;
}

export function updatePortfolioRecord(record: PortfolioRecord, user: AuthUser) {
  return savePortfolioRecord(record, user, "EDIT");
}

export async function deletePortfolioRecord(
  record: PortfolioRecord,
  user: AuthUser,
) {
  const payload = buildAuthPayload(user.email, user.token, {
    estateId: record.estateId,
    record: {
      id: record.id,
      type: "DELETE",
    },
  });
  const response = await savePortfolioRecords(payload, user.token);
  if (response.status !== 200 || !response.data?.success) {
    throw new Error(
      response.data?.message ?? "Failed to delete portfolio record",
    );
  }
}
